import React, { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Globe,
  Clock,
} from "lucide-react";
import { useLanguage } from "../../LanguageContext";

export default function ContactComponent() {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      setSubmitError("Please fill out all required fields.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitted(false);

    try {
      const response = await fetch("https://corsproxy.io/?url=https://api.resend.com/emails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer re_AhX3VQ5i_MwPE2kTe25gQPdVNiKEADmVU",
        },
        body: JSON.stringify({
          from: "Wrio Contact Form <onboarding@resend.dev>",
          to: ["wrio.super@gmail.com"],
          subject: `Wrio Contact: ${formData.subject}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 10px; background-color: #ffffff;">
              <h2 style="color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; margin-top: 0;">New Contact Form Submission</h2>
              <p style="color: #333333; font-size: 16px;">You have received a new message from the Wrio Static website contact form:</p>
              
              <table style="width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 15px;">
                <tr style="background-color: #f9f9f9;">
                  <td style="padding: 10px; font-weight: bold; width: 120px; border-bottom: 1px solid #eee; color: #555555;">Name:</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; color: #333333;">${formData.name}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; font-weight: bold; border-bottom: 1px solid #eee; color: #555555;">Email:</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee;"><a href="mailto:${formData.email}" style="color: #10b981; text-decoration: none; font-weight: 500;">${formData.email}</a></td>
                </tr>
                <tr style="background-color: #f9f9f9;">
                  <td style="padding: 10px; font-weight: bold; border-bottom: 1px solid #eee; color: #555555;">Phone:</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee;"><a href="tel:${formData.phone}" style="color: #10b981; text-decoration: none; font-weight: 500;">${formData.phone || "Not provided"}</a></td>
                </tr>
                <tr>
                  <td style="padding: 10px; font-weight: bold; border-bottom: 1px solid #eee; color: #555555;">Subject:</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; color: #333333; font-weight: 500;">${formData.subject}</td>
                </tr>
              </table>
              
              <div style="margin-top: 25px; padding: 15px 20px; background-color: #f0fdf4; border-left: 4px solid #10b981; border-radius: 4px;">
                <p style="margin: 0 0 8px 0; font-weight: bold; color: #166534; font-size: 15px;">Message Detail:</p>
                <p style="margin: 0; white-space: pre-wrap; color: #1f2937; line-height: 1.6; font-size: 14.5px;">${formData.message}</p>
              </div>
              
              <hr style="border: 0; border-top: 1px solid #e5e7eb; margin: 30px 0 20px 0;" />
              <p style="font-size: 12px; color: #9ca3af; text-align: center; margin: 0;">This email was securely delivered from your website's contact form via Resend.</p>
            </div>
          `,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        let errorDetails = "Failed to send message. Please try again.";
        try {
          const result = await response.json();
          if (result && result.message) {
            errorDetails = result.message;
          }
        } catch (e) { }
        setSubmitError(errorDetails);
      }
    } catch (err) {
      setSubmitError("Failed to connect to the email service. Please check your internet connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            {t("contact_banner")}
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {t("contact_title_prefix")}{" "}
            <span className="text-emerald-500">
              {t("contact_title_highlight")}
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t("contact_subheading")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Email */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t("contact_email_title")}
            </h3>
            <p className="text-gray-600 mb-4">{t("contact_email_note")}</p>
            <a
              href={`mailto:${t("contact_email")}`}
              className="text-emerald-600 font-medium hover:text-emerald-700"
            >
              {t("contact_email")}
            </a>
          </div>

          {/* Phone */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <Phone className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t("contact_call_title")}
            </h3>
            <p className="text-gray-600 mb-4">{t("contact_call_note")}</p>
            <a
              href={`tel:${t("contact_phone")}`}
              className="text-emerald-600 font-medium hover:text-emerald-700"
            >
              {t("contact_phone")}
            </a>
          </div>

          {/* Address */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <MapPin className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t("contact_visit_title")}
            </h3>
            <p className="text-gray-600 mb-4">{t("contact_visit_note")}</p>
            <p className="text-emerald-600 font-medium">
              {t("contact_address")}
            </p>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <div id="contact-form" className="scroll-mt-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-6">
              {t("contact_form_title")}
            </h2>

            {submitted && (
              <div className="mb-6 bg-white bg-opacity-20 border border-white border-opacity-40 text-white px-4 py-3 rounded-lg backdrop-blur-sm">
                {t("contact_thanks")}
              </div>
            )}

            {submitError && (
              <div className="mb-6 bg-red-500 bg-opacity-25 border border-red-500 border-opacity-40 text-white px-4 py-3 rounded-lg backdrop-blur-sm text-sm">
                {submitError}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("contact_form_name_label")}
                </label>
                <input
                  type="text"
                  name="name"
                  disabled={isSubmitting}
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none transition text-white placeholder-white placeholder-opacity-70 backdrop-blur-sm disabled:opacity-50"
                  placeholder={t("contact_form_name_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("contact_form_email_label")}
                </label>
                <input
                  type="email"
                  name="email"
                  disabled={isSubmitting}
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none transition text-white placeholder-white placeholder-opacity-70 backdrop-blur-sm disabled:opacity-50"
                  placeholder={t("contact_form_email_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("contact_form_phone_label")}
                </label>
                <input
                  type="text"
                  name="phone"
                  disabled={isSubmitting}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none transition text-white placeholder-white placeholder-opacity-70 backdrop-blur-sm disabled:opacity-50"
                  placeholder={t("contact_form_phone_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("contact_form_subject_label")}
                </label>
                <input
                  type="text"
                  name="subject"
                  disabled={isSubmitting}
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none transition text-white placeholder-white placeholder-opacity-70 backdrop-blur-sm disabled:opacity-50"
                  placeholder={t("contact_form_subject_placeholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("contact_form_message_label")}
                </label>
                <textarea
                  name="message"
                  disabled={isSubmitting}
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none transition resize-none text-white placeholder-white placeholder-opacity-70 backdrop-blur-sm disabled:opacity-50"
                  placeholder={t("contact_form_message_placeholder")}
                ></textarea>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full bg-white text-emerald-600 py-3 px-6 rounded-lg font-medium hover:bg-opacity-90 transition flex items-center justify-center gap-2 shadow-lg disabled:opacity-75 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-emerald-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    {t("contact_form_send")}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-4">
                {t("contact_why_title")}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Globe className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">
                      {t("contact_why_item1_title")}
                    </p>
                    <p className="text-emerald-50 text-sm">
                      {t("contact_why_item1_desc")}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">
                      {t("contact_why_item2_title")}
                    </p>
                    <p className="text-emerald-50 text-sm">
                      {t("contact_why_item2_desc")}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquare className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">
                      {t("contact_why_item3_title")}
                    </p>
                    <p className="text-emerald-50 text-sm">
                      {t("contact_why_item3_desc")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("contact_faq_title")}
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {t("contact_faq_q1")}
                  </p>
                  <p className="text-gray-600 text-sm">{t("contact_faq_a1")}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {t("contact_faq_q2")}
                  </p>
                  <p className="text-gray-600 text-sm">{t("contact_faq_a2")}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {t("contact_faq_q3")}
                  </p>
                  <p className="text-gray-600 text-sm">{t("contact_faq_a3")}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("contact_quick_stats_title")}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">
                    {t("contact_quick_stat_1_value")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("contact_quick_stat_1_label")}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">
                    {t("contact_quick_stat_2_value")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("contact_quick_stat_2_label")}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">
                    {t("contact_quick_stat_3_value")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("contact_quick_stat_3_label")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
